import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { PageShell, StatCard } from '../../components/Ui';
import BillingReceipts from '../billing/BillingReceipts';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { HMS_MODULES } from '../../config/modules';
import { formatHospitalAddress, printAnalyticsReport } from '../../utils/hospitalPdf';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import LetterheadPreview from '../../components/LetterheadPreview';
import LetterheadPdfToggle from '../../components/LetterheadPdfToggle';
import OpQueue from '../reception/OpQueue';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'month', label: 'This month' },
  { id: 'year', label: 'This year' },
  { id: 'all', label: 'All time' },
];

export function AnalysisPage() {
  const [period, setPeriod] = useState('month');
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { data: stats, loading, error } = useLiveData(() => ipApi.getAnalytics({ period }), [period]);
  const periodLabel = PERIODS.find((p) => p.id === period)?.label || period;

  return (
    <PageShell
      title="Analysis & Reports"
      subtitle="Revenue, occupancy & export PDF"
      actions={
        <button type="button" className="btn btn-secondary" disabled={loading || !stats} onClick={() => printAnalyticsReport(stats, hospital, periodLabel, user)}>
          Export PDF
        </button>
      }
    >
      <div className="period-tabs card mb">
        {PERIODS.map((p) => (
          <button key={p.id} type="button" className={`emr-chip ${period === p.id ? 'active' : ''}`} onClick={() => setPeriod(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
      {error && <div className="error mb">{error}</div>}
      <div className="stat-grid">
        <StatCard label="OP collected" value={loading ? '…' : `₹${Number(stats?.opReceiptsTotal || 0).toLocaleString('en-IN')}`} accent="#10b981" />
        <StatCard label="Receipts" value={loading ? '…' : stats?.receiptCount ?? 0} />
        <StatCard label="Completed visits" value={loading ? '…' : stats?.completedVisits ?? 0} accent="#4f8ef7" />
        <StatCard label="Tele-consults" value={loading ? '…' : stats?.teleconsultCount ?? 0} accent="#6366f1" />
        <StatCard label="Avg OP receipt" value={loading ? '…' : `₹${Number(stats?.avgOpReceipt || 0).toLocaleString('en-IN')}`} />
        <StatCard label="IP outstanding" value={loading ? '…' : `₹${Number(stats?.ipOutstanding || 0).toLocaleString('en-IN')}`} accent="#f97316" />
        <StatCard label="IP admissions" value={loading ? '…' : stats?.ipAdmissions ?? 0} />
        <StatCard label="Patients" value={loading ? '…' : stats?.patientCount ?? 0} />
        <StatCard label="OP in queue" value={loading ? '…' : stats?.opInQueue ?? 0} />
        <StatCard label="New patients" value={loading ? '…' : stats?.newPatients ?? 0} accent="#8b5cf6" />
      </div>
      {stats?.doctorBreakdown?.length > 0 && (
        <div className="card mt">
          <h3>Consultations by doctor</h3>
          <table className="data-table">
            <thead><tr><th>Doctor</th><th>Completed visits</th></tr></thead>
            <tbody>
              {stats.doctorBreakdown.map((d) => (
                <tr key={d.doctor}><td>{d.doctor}</td><td>{d.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {stats?.timeline?.length > 0 && (
        <div className="card mt">
          <h3>OP revenue timeline</h3>
          <ul className="simple-list">
            {stats.timeline.map((t) => (
              <li key={t.date}>{t.date}: ₹{Number(t.amount).toLocaleString('en-IN')}</li>
            ))}
          </ul>
        </div>
      )}
    </PageShell>
  );
}

export function SettingsPage() {
  const { user } = useAuth();
  const { data, loading, reload } = useLiveData(() => ipApi.getStaff(), []);
  const { hospital: dirHospital, reload: reloadDir } = useHospitalProfile();
  const hospital = dirHospital || data?.hospital;
  const quotas = data?.quotas;
  const [profileForm, setProfileForm] = useState(null);
  const [smsForm, setSmsForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [smsMsg, setSmsMsg] = useState('');
  const [lhOn, setLhOn] = useState(true);

  useEffect(() => {
    if (hospital && !profileForm) {
      setProfileForm({
        addressLine1: hospital.addressLine1 || '',
        addressLine2: hospital.addressLine2 || '',
        city: hospital.city || '',
        state: hospital.state || 'AP',
        pincode: hospital.pincode || '',
        phone: hospital.phone || '',
        email: hospital.email || '',
        gstin: hospital.gstin || '',
        tagline: hospital.tagline || '',
        regNo: hospital.regNo || '',
        website: hospital.website || '',
        letterheadColor: hospital.letterheadColor || '#1e40af',
      });
    }
    if (hospital && !smsForm) {
      setSmsForm({
        smsGatewayEnabled: hospital.smsGatewayEnabled === true,
        smsProvider: hospital.smsProvider || '',
        smsSenderId: hospital.smsSenderId || '',
        smsApiKey: '',
      });
    }
  }, [hospital, profileForm, smsForm]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await ipApi.patchHospitalProfile(profileForm);
      setMsg('Letterhead profile saved.');
      await reload();
      await reloadDir();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveSmsGateway = async (e) => {
    e.preventDefault();
    setSmsBusy(true);
    setSmsMsg('');
    try {
      const payload = {
        smsGatewayEnabled: smsForm.smsGatewayEnabled,
        smsProvider: smsForm.smsProvider,
        smsSenderId: smsForm.smsSenderId,
      };
      if (smsForm.smsApiKey?.trim()) payload.smsApiKey = smsForm.smsApiKey.trim();
      await ipApi.patchHospitalProfile(payload);
      setSmsMsg('SMS gateway settings saved.');
      setSmsForm((f) => ({ ...f, smsApiKey: '' }));
      await reload();
      await reloadDir();
    } catch (err) {
      setSmsMsg(err.message);
    } finally {
      setSmsBusy(false);
    }
  };

  const addrPreview = formatHospitalAddress(hospital);

  useEffect(() => {
    if (hospital) setLhOn(hospital.letterheadOnPdf !== false);
  }, [hospital?.letterheadOnPdf, hospital]);

  const previewHospital = profileForm && hospital
    ? { ...hospital, ...profileForm, letterheadOnPdf: lhOn }
    : hospital ? { ...hospital, letterheadOnPdf: lhOn } : null;

  return (
    <PageShell title="Settings" subtitle="Hospital profile & PDF options">
      <LetterheadPreview
        hospital={previewHospital}
        toolbar={user?.role === 'manager' ? (
          <LetterheadPdfToggle
            enabled={lhOn}
            onSaved={(v) => { setLhOn(v); reloadDir(); }}
          />
        ) : null}
      />
      <div className="form-grid">
        <div className="card">
          <h3>Your hospital</h3>
          <dl className="credential-dl">
            <dt>Name</dt><dd>{hospital?.name || user?.hospital || '—'}</dd>
            <dt>Address (on PDFs)</dt><dd style={{ whiteSpace: 'pre-line' }}>{addrPreview || '— Add address below —'}</dd>
            <dt>Plan</dt><dd>{hospital?.plan || '—'}</dd>
            <dt>City</dt><dd>{hospital?.city || '—'}</dd>
            <dt>Login URL</dt><dd><code>https://networks.hcen.in/login</code></dd>
            <dt>Staff email domain</dt><dd><code>@{hospital?.slug || 'hospital'}.hcen</code></dd>
          </dl>
        </div>
        <div className="card">
          <h3>Your account</h3>
          <dl className="credential-dl">
            <dt>Name</dt><dd>{user?.name}</dd>
            <dt>Email</dt><dd>{user?.email}</dd>
            <dt>Role</dt><dd>{user?.roleLabel}</dd>
            <dt>Hospital ID</dt><dd><code>{user?.hospitalId}</code></dd>
          </dl>
        </div>
      </div>
      {quotas && (
        <div className="card mt">
          <h3>Login limits (from HCEN)</h3>
          <p className="hint">Used {quotas.totalUsed} of {quotas.totalMax} staff logins</p>
        </div>
      )}
      {user?.role === 'manager' && profileForm && (
        <form className="card mt form-grid" onSubmit={saveProfile}>
          <h3>Letterhead profile (manager)</h3>
          <p className="hint full">Enter once — used automatically on receipts, Rx, lab, pharmacy, discharge &amp; reports.</p>
          <div className="field full"><label>Tagline</label><input className="input" placeholder="Quality care · Trusted healthcare" value={profileForm.tagline} onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })} /></div>
          <div className="field full"><label>Street address *</label><input className="input" required value={profileForm.addressLine1} onChange={(e) => setProfileForm({ ...profileForm, addressLine1: e.target.value })} /></div>
          <div className="field full"><label>Address line 2</label><input className="input" value={profileForm.addressLine2} onChange={(e) => setProfileForm({ ...profileForm, addressLine2: e.target.value })} /></div>
          <div className="field"><label>City</label><input className="input" value={profileForm.city} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} /></div>
          <div className="field"><label>State</label><input className="input" value={profileForm.state} onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })} /></div>
          <div className="field"><label>Pincode</label><input className="input" value={profileForm.pincode} onChange={(e) => setProfileForm({ ...profileForm, pincode: e.target.value })} /></div>
          <div className="field"><label>Phone</label><input className="input" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} /></div>
          <div className="field"><label>Email</label><input className="input" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} /></div>
          <div className="field"><label>GSTIN</label><input className="input" value={profileForm.gstin} onChange={(e) => setProfileForm({ ...profileForm, gstin: e.target.value })} /></div>
          <div className="field"><label>Registration no.</label><input className="input" placeholder="Hospital reg / clinical est." value={profileForm.regNo} onChange={(e) => setProfileForm({ ...profileForm, regNo: e.target.value })} /></div>
          <div className="field"><label>Website</label><input className="input" placeholder="www.hospital.in" value={profileForm.website} onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })} /></div>
          <div className="field"><label>Brand color</label><input className="input" type="color" value={profileForm.letterheadColor} onChange={(e) => setProfileForm({ ...profileForm, letterheadColor: e.target.value })} /></div>
          <div className="form-actions full">
            <button type="submit" className="btn btn-primary" disabled={busy}>Save letterhead</button>
          </div>
          {msg && <p className={`hint full ${msg.includes('saved') ? '' : 'error'}`}>{msg}</p>}
        </form>
      )}
      {user?.role === 'manager' && smsForm && (
        <form className="card mt form-grid" onSubmit={saveSmsGateway}>
          <h3>SMS gateway (manager)</h3>
          <p className="hint full">Configure MSG91 or Twilio for automated SMS delivery. API key is stored securely and never shown again after save.</p>
          <div className="field full">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={smsForm.smsGatewayEnabled} onChange={(e) => setSmsForm({ ...smsForm, smsGatewayEnabled: e.target.checked })} />
              Enable SMS gateway
            </label>
          </div>
          <div className="field">
            <label>Provider</label>
            <select className="input" value={smsForm.smsProvider} onChange={(e) => setSmsForm({ ...smsForm, smsProvider: e.target.value })}>
              <option value="">Not configured</option>
              <option value="msg91">MSG91</option>
              <option value="twilio">Twilio</option>
            </select>
          </div>
          <div className="field">
            <label>Sender ID</label>
            <input className="input" placeholder="HCENHP" value={smsForm.smsSenderId} onChange={(e) => setSmsForm({ ...smsForm, smsSenderId: e.target.value })} />
          </div>
          <div className="field full">
            <label>API key {hospital?.smsApiKeySet ? '(leave blank to keep existing)' : '*'}</label>
            <input className="input" type="password" autoComplete="new-password" placeholder={hospital?.smsApiKeySet ? '••••••••' : 'Enter provider API key'} value={smsForm.smsApiKey} onChange={(e) => setSmsForm({ ...smsForm, smsApiKey: e.target.value })} />
          </div>
          <div className="form-actions full">
            <button type="submit" className="btn btn-primary" disabled={smsBusy}>Save SMS settings</button>
          </div>
          {smsMsg && <p className={`hint full ${smsMsg.includes('saved') ? '' : 'error'}`}>{smsMsg}</p>}
        </form>
      )}
      {user?.role === 'manager' && (
        <div className="card mt">
          <h3>Manager tools</h3>
          <div className="row-actions">
            <Link to="/manager/admin" className="btn btn-primary">Staff &amp; logins</Link>
            <Link to="/manager/dashboard" className="btn btn-secondary">Dashboard</Link>
            <Link to="/hms/analysis" className="btn btn-secondary">Reports</Link>
          </div>
        </div>
      )}
      {loading && <p className="hint mt">Loading hospital info…</p>}
    </PageShell>
  );
}

export function BillingOpPage() {
  return <BillingReceipts />;
}

export default function HmsModulePage({ moduleId: propId }) {
  const { moduleId: paramId } = useParams();
  const moduleId = propId || paramId || '';
  const mod = HMS_MODULES.find((m) => m.id === moduleId);
  if (mod?.path) return <Navigate to={mod.path} replace />;
  return (
    <PageShell title="Module not found" subtitle={moduleId}>
      <div className="card">
        <p className="hint">No page is registered for “{moduleId}”. Open the module from the sidebar navigation.</p>
      </div>
    </PageShell>
  );
}

export function AppointmentsModulePage() {
  return (
    <OpQueue
      title="Appointments & OP Queue"
      subtitle="Booked · check-in · receipt · doctor — filter by doctor like DocPulse calendar view"
    />
  );
}
